import { toast } from 'react-hot-toast';

/**
 * Validates the full PR form (header + items). Shows a toast and returns false
 * on the first failure; returns true when valid. Shared by Create and Edit.
 */
export function validatePRForm(formData) {
  if (!formData.document_type) {
    toast.error('Document Type is required');
    return false;
  }
  if (formData.items.length === 0) {
    toast.error('At least one item is required');
    return false;
  }

  for (let i = 0; i < formData.items.length; i++) {
    const item = formData.items[i];
    const itemNum = item.item_number;

    if (!item.short_text.trim()) {
      toast.error(`Item ${itemNum}: Short text is required`);
      return false;
    }
    // null/undefined = not selected; '' is a valid Standard Item
    if (item.item_category === null || item.item_category === undefined) {
      toast.error(`Item ${itemNum}: Item category is required`);
      return false;
    }

    const materialRequiredCategories = ['', 'K', 'L', 'U'];
    if (materialRequiredCategories.includes(item.item_category) && !item.material_id) {
      toast.error(`Item ${itemNum}: Material is mandatory for this item category`);
      return false;
    }
    if (item.item_category === 'D' && item.material_id) {
      toast.error(`Item ${itemNum}: Service items should not have a material number`);
      return false;
    }
    if (!item.quantity || parseFloat(item.quantity) <= 0) {
      toast.error(`Item ${itemNum}: Quantity must be greater than 0`);
      return false;
    }
    if (!item.unit_of_measure) {
      toast.error(`Item ${itemNum}: Unit of Measure is required`);
      return false;
    }
    if (!item.delivery_date) {
      toast.error(`Item ${itemNum}: Delivery date is required`);
      return false;
    }
    if (!item.plant) {
      toast.error(`Item ${itemNum}: Plant is required`);
      return false;
    }
    if (!item.purchasing_group) {
      toast.error(`Item ${itemNum}: Purchasing group is required`);
      return false;
    }
    if (!item.purchasing_organization) {
      toast.error(`Item ${itemNum}: Purchasing organization is required`);
      return false;
    }
    if (!item.account_assignment_category) {
      toast.error(`Item ${itemNum}: Account Assignment Category is required`);
      return false;
    }

    if (item.account_assignment_category === 'K') {
      if (!item.cost_center) {
        toast.error(`Item ${itemNum}: Cost Center is mandatory for Account Assignment Category 'K'`);
        return false;
      }
      if (!item.gl_account) {
        toast.error(`Item ${itemNum}: G/L Account is mandatory for Account Assignment Category 'K'`);
        return false;
      }
    } else if (item.account_assignment_category === 'A') {
      if (!item.asset_number) {
        toast.error(`Item ${itemNum}: Asset Number is mandatory for Account Assignment Category 'A'`);
        return false;
      }
      if (!item.gl_account) {
        toast.error(`Item ${itemNum}: G/L Account is mandatory for Account Assignment Category 'A'`);
        return false;
      }
    } else if (item.account_assignment_category === 'F') {
      if (!item.order_number) {
        toast.error(`Item ${itemNum}: Order Number is mandatory for Account Assignment Category 'F'`);
        return false;
      }
    } else if (item.account_assignment_category === 'P') {
      if (!item.wbs_element) {
        toast.error(`Item ${itemNum}: WBS Element is mandatory for Account Assignment Category 'P'`);
        return false;
      }
    }

    if (item.request_date && item.delivery_date) {
      if (new Date(item.request_date) > new Date(item.delivery_date)) {
        toast.error(`Item ${itemNum}: Request date cannot be after delivery date`);
        return false;
      }
    }
    if (item.release_date && item.request_date) {
      if (new Date(item.release_date) < new Date(item.request_date)) {
        toast.error(`Item ${itemNum}: Release date cannot be before request date`);
        return false;
      }
    }
  }

  return true;
}

const NULLABLE_STRING_FIELDS = [
  'valuation_type', 'desired_vendor', 'fixed_vendor', 'service_number',
  'batch', 'manufacturer_part_number', 'title', 'name',
  'delivery_street_house_number', 'delivery_country', 'delivery_postal_code',
  'mrp_controller', 'contact_person', 'request_date', 'release_date',
  'per_unit_uom', 'gl_account', 'asset_number', 'order_number',
  'network', 'wbs_element', 'profit_center', 'co_area', 'company_code',
  'material_group', 'storage_location', 'agreement_number', 'info_record_number',
];

const NULLABLE_NUMBER_FIELDS = [
  'delivery_date_flexibility', 'valuation_price', 'estimated_unit_price',
  'total_value', 'po_item', 'quantity_ordered',
];

/** Converts empty optional fields to null and returns a submit-ready payload. */
export function cleanPRFormData(formData) {
  const cleanedItems = formData.items.map((item) => {
    const cleanedItem = { ...item };
    NULLABLE_STRING_FIELDS.forEach((f) => {
      if (cleanedItem[f] === '') cleanedItem[f] = null;
    });
    NULLABLE_NUMBER_FIELDS.forEach((f) => {
      if (cleanedItem[f] === '' || cleanedItem[f] === undefined) cleanedItem[f] = null;
    });
    return cleanedItem;
  });
  return { ...formData, items: cleanedItems };
}
